import type { Entity, EntityAction } from '../../components/AdminSettings/Entity/types';
import { getOptionsFromEnum } from '../../components/AdminSettings/Entity/utils';
import { NotebookKernelEntityType } from '../../notebook/entity/types';
import { ImagePullPolicy } from '../gen/ai/h2o/engine/v1/base_types_pb';
import { SandboxEngineImage } from '../gen/ai/h2o/engine/v1/sandbox_engine_image_pb';
import {
  type CreateSandboxEngineImageRequest,
  type CreateSandboxEngineImageResponse,
  type DeleteSandboxEngineImageRequest,
  type DeleteSandboxEngineImageResponse,
  type GetSandboxEngineImageRequest,
  type GetSandboxEngineImageResponse,
  type ListSandboxEngineImagesRequest,
  type ListSandboxEngineImagesResponse,
  SandboxEngineImageService_CreateSandboxEngineImage,
  SandboxEngineImageService_DeleteSandboxEngineImage,
  SandboxEngineImageService_GetSandboxEngineImage,
  SandboxEngineImageService_ListSandboxEngineImages,
  SandboxEngineImageService_UpdateSandboxEngineImage,
  type UpdateSandboxEngineImageRequest,
  type UpdateSandboxEngineImageResponse,
} from '../gen/ai/h2o/engine/v1/sandbox_engine_image_service_pb';
import { engineImageColumns, EntityActionType, EntityFieldType } from './constants';
import type { EntityField } from './types';

const entityType = NotebookKernelEntityType.SandboxImage;
const required = true;

export const actions = {
  [EntityActionType.Create]: {
    name: 'CreateSandboxEngineImage',
    rpc: SandboxEngineImageService_CreateSandboxEngineImage,
    type: EntityActionType.Create,
    responseKey: 'sandboxEngineImage',
    requestNameKey: 'sandboxEngineImageId',
    requestPayloadKey: 'sandboxEngineImage',
    requestNameKeyPrefix: 'sandboxEngineImage/',
    entityType,
  } as EntityAction<
    SandboxEngineImage,
    NotebookKernelEntityType,
    CreateSandboxEngineImageRequest,
    CreateSandboxEngineImageResponse
  >,
  [EntityActionType.Get]: {
    name: 'GetSandboxEngineImage',
    rpc: SandboxEngineImageService_GetSandboxEngineImage,
    type: EntityActionType.Get,
    responseKey: 'sandboxEngineImage',
    entityType,
  } as EntityAction<
    SandboxEngineImage,
    NotebookKernelEntityType,
    GetSandboxEngineImageRequest,
    GetSandboxEngineImageResponse
  >,
  [EntityActionType.List]: {
    name: 'ListSandboxEngineImage',
    rpc: SandboxEngineImageService_ListSandboxEngineImages,
    type: EntityActionType.List,
    responseKey: 'sandboxEngineImages',
    entityType,
  } as EntityAction<
    SandboxEngineImage,
    NotebookKernelEntityType,
    ListSandboxEngineImagesRequest,
    ListSandboxEngineImagesResponse
  >,
  [EntityActionType.Update]: {
    name: 'UpdateSandboxEngineImage',
    rpc: SandboxEngineImageService_UpdateSandboxEngineImage,
    type: EntityActionType.Update,
    entityType,
    responseKey: 'sandboxEngineImage',
    requestNameKey: 'name' as unknown,
    requestPayloadKey: 'sandboxEngineImage',
  } as EntityAction<
    SandboxEngineImage,
    NotebookKernelEntityType,
    UpdateSandboxEngineImageRequest,
    UpdateSandboxEngineImageResponse
  >,
  [EntityActionType.Delete]: {
    name: 'DeleteSandboxEngineImage',
    rpc: SandboxEngineImageService_DeleteSandboxEngineImage,
    type: EntityActionType.Delete,
    entityType,
  } as EntityAction<
    SandboxEngineImage,
    NotebookKernelEntityType,
    DeleteSandboxEngineImageRequest,
    DeleteSandboxEngineImageResponse
  >,
};

export const fields: EntityField<SandboxEngineImage>[] = [
  {
    name: 'name',
    type: EntityFieldType.IdOnDisplayAndId,
    label: 'Name',
    description: 'Sandbox Image resource name.',
    required,
  },
  {
    name: 'displayName',
    type: EntityFieldType.DisplayOnDisplayAndId,
    label: 'Display Name',
    description: 'Human-readable name of Sandbox Image',
    required,
  },
  {
    name: 'enabled',
    type: EntityFieldType.Boolean,
    label: 'Enabled',
  },
  {
    name: 'image',
    type: EntityFieldType.Text,
    label: 'Image',
    required,
  },
  {
    name: 'imagePullPolicy',
    type: EntityFieldType.SelectEnum,
    label: 'Image pull policy',
    options: getOptionsFromEnum(ImagePullPolicy, 'IMAGE_PULL_POLICY_', [ImagePullPolicy.UNSPECIFIED]),
  },
  {
    name: 'imagePullSecrets',
    type: EntityFieldType.StringArray,
    label: 'Image pull secrets',
  },
];

export const sandboxEngineImageEntity: Entity<SandboxEngineImage, NotebookKernelEntityType> = {
  type: entityType,
  name: 'SandboxEngineImage',
  createButtonLabel: '',
  displayName: 'Sandbox Engine Image',
  displayNameKey: 'displayName',
  requestNameKeyPrefix: 'workspaces/global/sandboxEngineImages/',
  actions,
  columns: engineImageColumns,
  fields,
  emptyModel: {
    name: 'new-sandbox-engine-image',
    displayName: 'New Sandbox Engine Image',
    image: '',
    enabled: true,
    imagePullPolicy: ImagePullPolicy.ALWAYS,
    imagePullSecrets: [],
  },
  canBeReordered: false,
};
